import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { Suspense } from "react";
import CHANGELOG_CONTEXT from "src/context/changelog";
import { useTranslation } from "react-i18next";
import TabLayout from "src/components/base/tab-layout";
import DEVICE_CONTEXT, { DeviceProvider } from "src/context/internal/device";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import RenameDeviceDialog from "src/components/dialogs/rename-device";
import ExpireDeviceKeyDialog from "src/components/dialogs/expire-device-key";
import DeleteDeviceDialog from "src/components/dialogs/delete-device";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { ChevronDownIcon, ClockIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import LinkExtraDomainDialog from "src/components/dialogs/link-extra-record";
import { TenantBadge } from "src/components/badges/tenant";
import { StatusBadge } from "src/components/badges/status";
import Outlet from "src/components/base/outlet";

/**
 * The properties for {@link DeviceTabs}
 */
export type DeviceTabsProps = {};

/**
 * Status pill row rendered between the heading and the tabs — surfaces the
 * three pieces of information operators glance at first when opening a device.
 */
function StatusStrip({ device }: { device: FullDevice }) {
    const [t] = useTranslation("admin-device-view");
    const expiry = device.expiry ? new Date(device.expiry) : null;
    const expired = expiry !== null && expiry.getTime() < Date.now();

    return (
        <div className={"flex flex-wrap items-center gap-2 text-sm"}>
            <StatusBadge online={device.online} lastSeen={device.last_seen} />

            {expiry && (
                <span
                    className={
                        expired
                            ? "rounded-pill inline-flex items-center gap-2 bg-red-500/10 px-2.5 py-1 font-medium text-red-600 dark:text-red-400"
                            : "rounded-pill inline-flex items-center gap-2 bg-zinc-100 px-2.5 py-1 font-medium text-zinc-600 dark:bg-white/5 dark:text-zinc-300"
                    }
                >
                    <ClockIcon className={"size-3.5"} />
                    {expired
                        ? t("label.key-expired", { defaultValue: "Key expired" })
                        : t("label.key-expires", {
                              defaultValue: "Expires {{date}}",
                              date: expiry.toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                              }),
                          })}
                </span>
            )}

            {device.ipv4 && (
                <span
                    className={
                        "rounded-pill inline-flex items-center gap-2 bg-zinc-100 px-2.5 py-1 font-mono text-xs text-zinc-700 dark:bg-white/5 dark:text-zinc-300"
                    }
                >
                    {device.ipv4}
                </span>
            )}

            <TenantBadge tenant={device.tenant} />
        </div>
    );
}

/**
 * Tabs for the device view
 */
function DeviceTabs(props: DeviceTabsProps) {
    const [t] = useTranslation("admin-device-view");
    const { isProd } = React.useContext(CHANGELOG_CONTEXT);

    const navigate = useNavigate();
    const router = useRouter();

    const { deviceId } = Route.useParams();
    const data = Route.useLoaderData();

    const [openRenameDevice, setOpenRenameDevice] = React.useState<FullDevice>();
    const [openExpireKey, setOpenExpireKey] = React.useState<FullDevice>();
    const [openDeleteDevice, setOpenDeleteDevice] = React.useState<FullDevice>();
    const [openAddExtraDomain, setOpenAddExtraDomain] = React.useState<string | undefined>(undefined);

    return (
        <DeviceProvider uuid={deviceId}>
            <DEVICE_CONTEXT.Consumer>
                {(deviceContext) => (
                    <>
                        <TabLayout
                            breadcrumbs={[
                                {
                                    label: t("button.devices"),
                                    to: "/a/devices",
                                    search: { search: "", filter: "all", tenant: "all" },
                                },
                                { label: deviceContext.device.dnsname },
                            ]}
                            heading={deviceContext.device.dnsname}
                            headingMeta={<StatusStrip device={deviceContext.device} />}
                            headingChildren={
                                <Dropdown>
                                    <DropdownButton outline={true}>
                                        <PencilSquareIcon />
                                        <span>{t("button.rename-device")}</span>
                                        <ChevronDownIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenRenameDevice(deviceContext.device)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.rename-device")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem
                                                disabled={
                                                    data.list.length === 0 ||
                                                    (!deviceContext.device.ipv4 && !deviceContext.device.ipv6)
                                                }
                                                onClick={() => {
                                                    if (deviceContext.device.ipv4) {
                                                        setOpenAddExtraDomain(deviceContext.device.ipv4);
                                                    } else if (deviceContext.device.ipv6) {
                                                        setOpenAddExtraDomain(deviceContext.device.ipv6);
                                                    }
                                                }}
                                            >
                                                <PlusCircleIcon />
                                                <DropdownLabel>{t("button.link-extra-record")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>

                                        <DropdownDivider />

                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                                            <DropdownItem onClick={() => setOpenExpireKey(deviceContext.device)}>
                                                <ClockIcon />
                                                <DropdownLabel>{t("button.expire-key")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenDeleteDevice(deviceContext.device)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-device")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            tabs={
                                <TabMenu>
                                    <Tab href={"/a/devices/$deviceId/general"} params={{ deviceId: deviceId }}>
                                        {t("heading.general")}
                                    </Tab>
                                    <Tab href={"/a/devices/$deviceId/inbound-acls"} params={{ deviceId: deviceId }}>
                                        {t("heading.inbound-acl")}
                                    </Tab>
                                    <Tab href={"/a/devices/$deviceId/outbound-acls"} params={{ deviceId: deviceId }}>
                                        {t("heading.outbound-acl")}
                                    </Tab>
                                    {!isProd && (
                                        <Tab href={"/a/devices/$deviceId/debug"} params={{ deviceId: deviceId }}>
                                            {t("heading.debug")}
                                        </Tab>
                                    )}
                                </TabMenu>
                            }
                        >
                            <Outlet />
                        </TabLayout>

                        <Suspense>
                            <RenameDeviceDialog
                                device={openRenameDevice}
                                isOpen={!!openRenameDevice}
                                onClose={() => setOpenRenameDevice(undefined)}
                                onRename={() => {
                                    deviceContext.reset();
                                    setOpenRenameDevice(undefined);
                                }}
                            />

                            <ExpireDeviceKeyDialog
                                device={openExpireKey}
                                isOpen={!!openExpireKey}
                                onClose={() => setOpenExpireKey(undefined)}
                                onExpire={() => {
                                    deviceContext.reset();
                                    setOpenExpireKey(undefined);
                                }}
                            />

                            <DeleteDeviceDialog
                                isOpen={!!openDeleteDevice}
                                onClose={() => setOpenDeleteDevice(undefined)}
                                onDeletion={async () => {
                                    setOpenDeleteDevice(undefined);
                                    await navigate({
                                        to: "/a/devices",
                                        search: { search: "", filter: "all", tenant: "all", page: 1 },
                                    });
                                }}
                                device={openDeleteDevice}
                            />

                            <LinkExtraDomainDialog
                                device_ip={openAddExtraDomain}
                                domains={data.list}
                                isOpen={!!openAddExtraDomain}
                                onSubmit={async () => {
                                    await router.invalidate();
                                    setOpenAddExtraDomain(undefined);
                                }}
                                onClose={() => setOpenAddExtraDomain(undefined)}
                            />
                        </Suspense>
                    </>
                )}
            </DEVICE_CONTEXT.Consumer>
        </DeviceProvider>
    );
}

export const Route = createFileRoute("/_admin/a/devices/$deviceId/_device")({
    component: DeviceTabs,
    loader: async ({ params }) => await ApiInternal.dns.getPossibleExtraDnsByDevice(params.deviceId),
});
